import { FEATURES, FEATURE_NAMES } from "../constants/features";

/**
 * Check if user has access to a specific feature
 * @param featureCode - Feature code from FEATURES constant
 * @param availableFeatures - Array from permissions.availableFeatures
 * @returns boolean indicating if user has access
 */
export const hasFeature = (
  featureCode: number,
  availableFeatures: number[] = []
): boolean => {
  return availableFeatures.includes(featureCode);
};

/**
 * Check if user has access to ANY of the given features
 * @param featureCodes - Array of feature codes
 * @param availableFeatures - Array from permissions.availableFeatures
 * @returns boolean indicating if user has at least one feature
 */
export const hasAnyFeature = (
  featureCodes: number[],
  availableFeatures: number[] = []
): boolean => {
  return featureCodes.some((code) => availableFeatures.includes(code));
};

/**
 * Check if user has access to ALL of the given features
 * @param featureCodes - Array of feature codes
 * @param availableFeatures - Array from permissions.availableFeatures
 * @returns boolean indicating if user has all features
 */
export const hasAllFeatures = (
  featureCodes: number[],
  availableFeatures: number[] = []
): boolean => {
  return featureCodes.every((code) => availableFeatures.includes(code));
};

/**
 * Get list of missing features
 * @param requiredFeatures - Required feature codes
 * @param availableFeatures - User's available features
 * @returns Array of missing feature codes
 */
export const getMissingFeatures = (
  requiredFeatures: number[],
  availableFeatures: number[] = []
): number[] => {
  return requiredFeatures.filter((code) => !availableFeatures.includes(code));
};

/**
 * Get names of missing features
 * @param requiredFeatures - Required feature codes
 * @param availableFeatures - User's available features
 * @returns Array of missing feature names
 */
export const getMissingFeatureNames = (
  requiredFeatures: number[],
  availableFeatures: number[] = []
): string[] => {
  const missing = getMissingFeatures(requiredFeatures, availableFeatures);
  return missing.map((code) => FEATURE_NAMES[code] || `Feature ${code}`);
};

/**
 * Check if features are available and return detailed status
 * @param requiredFeatures - Required feature codes
 * @param availableFeatures - User's available features
 * @returns Object with hasAccess boolean and missing feature details
 */
export const checkFeatureAccess = (
  requiredFeatures: number | number[],
  availableFeatures: number[] = []
): {
  hasAccess: boolean;
  missingFeatures: number[];
  missingFeatureNames: string[];
} => {
  const required = Array.isArray(requiredFeatures)
    ? requiredFeatures
    : [requiredFeatures];
  const missing = getMissingFeatures(required, availableFeatures);

  return {
    hasAccess: missing.length === 0,
    missingFeatures: missing,
    missingFeatureNames: missing.map(
      (code) => FEATURE_NAMES[code] || `Feature ${code}`
    ),
  };
};

/**
 * Get recommended plan based on missing features
 * @param missingFeatures - Array of missing feature codes
 * @returns Recommended plan name
 */
export const getRecommendedPlan = (missingFeatures: number[]): string => {
  // Check if any Enterprise-only features are missing
  const enterpriseOnlyFeatures: number[] = [
    FEATURES.SMART_PRICING,
    FEATURES.ANALYTICS,
    FEATURES.ORDER_PROCESSING,
    FEATURES.HR_MANAGEMENT,
    FEATURES.LOYALTY_PROGRAM,
  ];

  const needsEnterprise = missingFeatures.some((feature) =>
    enterpriseOnlyFeatures.includes(feature)
  );

  if (needsEnterprise) {
    return "Enterprise";
  }

  // Check if any Pro-level features are missing
  const proFeatures: number[] = [
    FEATURES.REPORTS,
    FEATURES.COST_CALCULATOR,
    FEATURES.INVENTORY,
    FEATURES.RECIPE_LIBRARY,
    FEATURES.INTELLIGENT_MENU, // 🔥 UPDATED
    FEATURES.STAFF_MANAGEMENT,
    FEATURES.MANAGE_TABLES,
    FEATURES.GENERATE_QR,
  ];

  const needsPro = missingFeatures.some((feature) =>
    proFeatures.includes(feature)
  );

  if (needsPro) {
    return "Pro";
  }

  return "Basic";
};

/**
 * 🔥 CRITICAL FUNCTION: Check Intelligent Menu Tab Access
 *
 * This determines which TABS inside Intelligent Menu page are accessible.
 * Basic Plan has INTELLIGENT_MENU (14) but only Menu Management tab.
 * Pro Plan has INTELLIGENT_MENU (14) PLUS additional tabs.
 *
 * @param tabName - Tab name to check: 'menu-management', 'cost-calculator', 'inventory', 'recipe-library', 'ai-suggestions', 'smart-pricing', 'analytics'
 * @param availableFeatures - User's available feature codes
 * @returns boolean indicating tab accessibility
 */
export const canAccessIntelligentMenuTab = (
  tabName: string,
  availableFeatures: number[] = []
): boolean => {
  // Must have INTELLIGENT_MENU feature first
  if (!hasFeature(FEATURES.INTELLIGENT_MENU, availableFeatures)) {
    return false;
  }

  // Tab-specific access control
  switch (tabName.toLowerCase()) {
    case "menu-management":
      // Always accessible if user has INTELLIGENT_MENU (Basic+ plans)
      return true;

    case "cost-calculator":
      // Requires COST_CALCULATOR feature (Pro+ plans)
      return hasFeature(FEATURES.COST_CALCULATOR, availableFeatures);

    case "inventory":
      // Requires INVENTORY feature (Pro+ plans)
      return hasFeature(FEATURES.INVENTORY, availableFeatures);

    case "recipe-library":
      // Requires RECIPE_LIBRARY feature (Pro+ plans)
      return hasFeature(FEATURES.RECIPE_LIBRARY, availableFeatures);

    case "ai-suggestions":
      // Available if user has INTELLIGENT_MENU (Pro+ plans, not Basic)
      // Basic has INTELLIGENT_MENU but without AI suggestions
      // Pro+ has both INTELLIGENT_MENU and more features
      return hasFeature(FEATURES.COST_CALCULATOR, availableFeatures); // Use this as Pro+ indicator

    case "smart-pricing":
      // Requires SMART_PRICING feature (Enterprise only)
      return hasFeature(FEATURES.SMART_PRICING, availableFeatures);

    case "analytics":
      // Requires ANALYTICS feature (Enterprise only)
      return hasFeature(FEATURES.ANALYTICS, availableFeatures);

    default:
      // Unknown tab - deny access
      console.warn(`Unknown Intelligent Menu tab: ${tabName}`);
      return false;
  }
};

/**
 * Get accessible tabs for Intelligent Menu
 * @param availableFeatures - User's available feature codes
 * @returns Array of accessible tab names
 */
export const getAccessibleIntelligentMenuTabs = (
  availableFeatures: number[] = []
): string[] => {
  const allTabs = [
    "menu-management",
    "cost-calculator",
    "inventory",
    "recipe-library",
    "ai-suggestions",
    "smart-pricing",
    "analytics",
  ];

  return allTabs.filter((tab) =>
    canAccessIntelligentMenuTab(tab, availableFeatures)
  );
};
